import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ContactForm } from "@/components/program/ContactForm";
import { LogoUpload } from "@/components/program/LogoUpload";
import { APIClient, ClientApi, ProgramApi } from "@/api";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import countries from "@/constants/array/country_city.json";
import {
  PAYMENT_CURRENCY_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
  PROGRAM_TYPES,
} from "@/constants/program.constant";
import { LoaderButton } from "@/components/ui/loader-button";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import VirtualizedCountrySelect from "@/components/ui/virtualized-country-select";

const TransferProgramCreate = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [codeVerificationStatus, setCodeVerificationStatus] = useState<
    "available" | "taken" | null
  >(null);
  const [isVerifyingDomain, setIsVerifyingDomain] = useState(false);
  const [domainVerificationStatus, setDomainVerificationStatus] = useState<
    "available" | "taken" | null
  >(null);
  const form = useForm({
    resolver: yupResolver(ProgramSchema),
    defaultValues: {
      id: null,
      logo: null,
      name: "",
      code: "",
      programType: PROGRAM_TYPES.TRANSFER,
      clientId: "",
      address: "",
      paymentCurrency: "",
      costPerPoint: null,
      loyaltyPointsName: "",
      cashbackPercentage: null,
      txnCommissionPctChargedToPrg: null,
      vatNumber: "",
      invoiceFrequency: "",
      websiteUrl: "",
      domain: "",
      country: "",
      city: "",
      contacts: [
        {
          name: "",
          email: "",
          countryCode: "+971",
          phoneNumber: "",
          role: "",
        },
      ],
    },
  });

  const verifyProgramCode = async () => {
    const programCode = form.getValues("code");
    if (!programCode || programCode.trim() === "") {
      toast.error("Please enter a program code first");
      return;
    }

    setIsVerifyingCode(true);
    setCodeVerificationStatus(null);

    try {
      const response = await ProgramApi.verifyProgramDetails({
        programCode: programCode.trim(),
        domain: "",
      });

      const isProgramCodeExist = response.data?.isProgramCodeExist;

      if (isProgramCodeExist === false) {
        // Program code is available (doesn't exist)
        setCodeVerificationStatus("available");
        toast.success("Program code is available");
      } else if (isProgramCodeExist === true) {
        // Program code already exists
        setCodeVerificationStatus("taken");
        toast.error("Program code already exists");
      } else {
        // Unexpected response
        console.error("Unexpected API response:", response);
        toast.error("Unexpected response from server");
        setCodeVerificationStatus(null);
      }
    } catch (error) {
      console.error("Error verifying program code:", error);
      toast.error("Failed to verify program code");
      setCodeVerificationStatus(null);
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const verifyDomain = async () => {
    const domain = form.getValues("domain");
    if (!domain || domain.trim() === "") {
      toast.error("Please enter a domain first");
      return;
    }

    setIsVerifyingDomain(true);
    setDomainVerificationStatus(null);

    try {
      const programCode = form.getValues("code") || "";
      const response = await ProgramApi.verifyProgramDetails({
        programCode: programCode.trim(),
        domain: domain.trim(),
      });

      const isDomainExist = response.data?.isDomainExist;

      if (isDomainExist === false) {
        // Domain is available (doesn't exist)
        setDomainVerificationStatus("available");
        toast.success("Domain is available");
      } else if (isDomainExist === true) {
        // Domain already exists
        setDomainVerificationStatus("taken");
        toast.error("Domain already exists");
      } else {
        // Unexpected response
        console.error("Unexpected API response:", response);
        toast.error("Unexpected response from server");
        setDomainVerificationStatus(null);
      }
    } catch (error) {
      console.error("Error verifying domain:", error);
      toast.error("Failed to verify domain");
      setDomainVerificationStatus(null);
    } finally {
      setIsVerifyingDomain(false);
    }
  };

  const onSubmit = async (formData) => {
    // Check if program code is verified as available
    if (codeVerificationStatus !== "available") {
      toast.error(
        "Please verify that the program code is available before submitting"
      );
      return;
    }
    // Check if domain is verified as available
    if (domainVerificationStatus !== "available") {
      toast.error(
        "Please verify that the domain is available before submitting"
      );
      return;
    }
    setIsSubmitting(true);
    try {
      const { contacts } = formData;
      const formDataToSend = new FormData();
      formDataToSend.append("programLogo", formData?.logo);
      const currency = PAYMENT_CURRENCY_OPTIONS?.find(
        (currency) => String(currency?.id) === String(formData?.paymentCurrency)
      );
      const programData = {
        id: formData?.id,
        invoiceFrequencyInDays: Number(formData?.invoiceFrequency), // Ensure number type
        percentageOfCashbackToBeAccrued: formData?.cashbackPercentage,
        taxId: formData?.vatNumber,
        address: formData?.address,
        programType: formData?.programType,
        programCode: formData?.code,
        costPerPoint: formData?.costPerPoint,
        programName: formData?.name,
        loyaltyCurrencyId: formData?.loyaltyPointsName,
        currencyVO: { id: formData?.paymentCurrency },
        clientId: formData?.clientId,
        domain: formData?.domain,
        websiteUrl: formData?.websiteUrl,
        country: selectedCountry?.name,
        city: formData?.city,
        txnCommissionPctChargedToPrg: formData?.txnCommissionPctChargedToPrg,
      };
      const blob = new Blob([JSON.stringify(programData)], {
        type: "application/json",
      });
      formDataToSend.append("programVO", blob);

      const { data } = await APIClient(
        "/program/createOrUpdateProgram",
        formDataToSend,
        { requestType: "post", contentType: "multipart/form-data" }
      );
      form.setValue("id", data?.id);
      await Promise.all(
        contacts.map((contact) =>
          APIClient(
            "/program/createOrUpdateProgramContact",
            {
              programId: data?.id,
              contactName: contact?.name,
              contactEmail: contact?.email,
              contactRole: contact?.role,
              programContactPhoneVOList: [
                {
                  phoneCountryCode: contact?.countryCode,
                  phoneNumber: contact?.phoneNumber,
                },
              ],
            },
            { requestType: "post" }
          )
        )
      );

      // handleReset();
      toast.success("Partner created successfully");
      navigate(`/xpandpoint/partners`);
      // navigate(`/xpandpoint/programs`);
    } catch (error) {
      toast.error(error?.message || "Failed to update contacts");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getClients = async () => {
    try {
      const res = await ClientApi.getAllClients();
      setClients(
        (res?.data || [])?.map((client) => ({
          value: `${client?.id || ""}`,
          label: client?.clientName,
        }))
      );
    } catch (error) {
      toast.error(error?.message || "Failed to fetch clients");
    }
  };

  useEffect(() => {
    getClients();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/xpandpoint/partners">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Create New Transfer Partner
          </h1>
          <p className="text-gray-500">Create a new transfer partner</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Logo Upload */}
                <LogoUpload form={form} />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Emirates Skywards"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^a-zA-Z0-9\s\-':.]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        The name of your partner
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code*</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Input
                              placeholder="e.g. SKYW001"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                // Reset verification status when code changes
                                setCodeVerificationStatus(null);
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={verifyProgramCode}
                              disabled={isVerifyingCode || !field.value}
                              className="shrink-0"
                            >
                              {isVerifyingCode ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Verifying...
                                </>
                              ) : (
                                "Verify"
                              )}
                            </Button>
                          </div>
                          {codeVerificationStatus && (
                            <div className="flex items-center gap-2 text-sm">
                              {codeVerificationStatus === "available" ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  <span className="text-green-600">
                                    Program code is available
                                  </span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 text-red-600" />
                                  <span className="text-red-600">
                                    Program code already exists
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          {codeVerificationStatus !== "available" && (
                            <p className="text-sm text-gray-500">
                              Program code must be verified as available before
                              you can submit the form.
                            </p>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="programType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select partner type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PROGRAM_TYPE_OPTIONS?.map((program) => (
                            <SelectItem
                              key={program?.id}
                              value={program?.value}
                            >
                              {program?.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Client" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {clients?.map((client) => (
                            <SelectItem
                              key={client?.value}
                              value={client?.value}
                            >
                              {client?.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vatNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT Number</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. AE123456789" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. www.example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="domain"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Domain*</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Input
                              placeholder="e.g. example"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                // Reset verification status when domain changes
                                setDomainVerificationStatus(null);
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={verifyDomain}
                              disabled={isVerifyingDomain || !field.value}
                              className="shrink-0"
                            >
                              {isVerifyingDomain ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Verifying...
                                </>
                              ) : (
                                "Verify"
                              )}
                            </Button>
                          </div>
                          {domainVerificationStatus && (
                            <div className="flex items-center gap-2 text-sm">
                              {domainVerificationStatus === "available" ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  <span className="text-green-600">
                                    Domain is available
                                  </span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 text-red-600" />
                                  <span className="text-red-600">
                                    Domain already exists
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          {domainVerificationStatus !== "available" && (
                            <p className="text-sm text-gray-500">
                              Domain must be verified as available before you
                              can submit the form.
                            </p>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Loyalty and Billing and Address*/}
            <div className="space-y-8">
              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Loyalty Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <FormField
                    control={form.control}
                    name="loyaltyPointsName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Loyalty Points Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Skywards Miles" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="costPerPoint"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cost Per Point*</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. 0.012" {...field} />
                        </FormControl>
                        <FormDescription>
                          The cost per loyalty point in the selected currency
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cashbackPercentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Percentage of cashback to be Accrued*
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. 2.5%" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Billing Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <FormField
                    control={form.control}
                    name="paymentCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Currency*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PAYMENT_CURRENCY_OPTIONS.map((currency) => (
                              <SelectItem
                                key={currency?.id}
                                value={String(currency?.id)}
                              >{`${currency?.value} - ${currency?.label}`}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="invoiceFrequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invoice Frequency (days)*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 30"
                            type="number"
                            min={1}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="txnCommissionPctChargedToPrg"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Transaction Commission</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. 12" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Address*</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter company address"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <VirtualizedCountrySelect
                        countries={(countries as any[]) || []}
                        value={field.value || ""}
                        onValueChange={(name) => {
                          if (name !== field.value) {
                            form.setValue("city", "", { shouldDirty: true });
                            form.clearErrors("city");
                          }
                          const selected = (countries as any[])?.find(
                            (c) => c.name === name,
                          );
                          setSelectedCountry(selected || null);
                          field.onChange(name);
                        }}
                        placeholder="Select Country"
                        label="Country*"
                        error={form.formState.errors.country?.message}
                        withForm
                      />
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City*</FormLabel>
                        <VirtualizedCitySelect
                          cities={selectedCountry?.cities || []}
                          value={field.value || ""}
                          onValueChange={(value) => {
                            form.setValue("city", value, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                          placeholder="Select City"
                          disabled={!selectedCountry?.cities?.length}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Information */}
          <Card className="border-0 shadow-sm overflow-hidden bg-white">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Contact Information
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Add one or more contact persons for this partner
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <ContactForm form={form} />
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/xpandpoint/partners")}
            >
              Cancel
            </Button>
            <LoaderButton type="submit" loading={isSubmitting}>
              <Save className="h-4 w-4 mr-2" /> Create Partner
            </LoaderButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default TransferProgramCreate;

const ProgramSchema = Yup.object().shape({
  id: Yup.string().optional().nullable(),
  logo: Yup.mixed().required("Logo is required"),
  name: Yup.string()
    .required("Partner Name is required")
    .matches(
      /^[a-zA-Z0-9\s\-':.]+$/,
      "Partner name can only contain letters, numbers, spaces, and the following special characters: - ' : ."
    ),
  address: Yup.string().required("Partner Location is required"),
  costPerPoint: Yup.number()
    .required("CPP is required")
    .positive("Cost per point must be a positive number")
    .typeError("Must be a number"),
  code: Yup.string()
    .required("Partner code is required")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "Partner code can only contain letters and numbers (no special characters)"
    ),
  programType: Yup.string().required("Partner Type is required"),
  websiteUrl: Yup.string()
    .required("Website url is required")
    .url("Website URL must be a valid HTTP URL format"),
  domain: Yup.string().required("Domain is required"),
  clientId: Yup.string().required("Client is required"),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),
  vatNumber: Yup.string(),
  paymentCurrency: Yup.string().required("Payment Currency is required"),
  loyaltyPointsName: Yup.string().required("Loyalty Points Name is required"),
  txnCommissionPctChargedToPrg: Yup.number()
    .optional()
    .nullable()
    .positive("Transaction commission must be a positive number")
    .max(100, "Percentage cannot be greater than 100")
    .typeError("Must be a number"),
  cashbackPercentage: Yup.number()
    .required("Percentage of cashback to be Accrued is required")
    .positive("Percentage must be a positive number")
    .max(100, "Percentage cannot be greater than 100")
    .typeError("Must be a number"),
  invoiceFrequency: Yup.string().required("Invoice frequency is required"),

  contacts: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required("Contact name is required")
        .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
      email: Yup.string()
        .required("Email is required")
        .email("Email must be a valid email address"),
      role: Yup.string()
        .required("Role is required")
        .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
      countryCode: Yup.string(),
      phoneNumber: Yup.string()
        .required("Phone is required")
        .matches(/^\d+$/, "Phone must contain digits only")
        .min(7, "Phone number must be at least 7 digits")
        .max(15, "Phone number cannot exceed 15 digits"),
    })
  ),
});
